import { useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import {
  Icon,
  V2Avatar,
  V2Button,
  V2Card,
  V2CardHeader,
  V2EmptyState,
  type SakhtarIconName,
} from '@sakhtar/design-system/react';
import { apiPatch, apiPost, ApiError } from '../../lib/apiClient.js';
import { faDigits } from '../../lib/faDigits.js';
import { formatRialFaWithUnit, type AmountDisplayUnit } from '../../lib/finance-display.js';
import { displayJalaliDateTime } from '../../lib/date/displayJalali.js';
import { relativeWhen } from '../../lib/date/relativeWhen.js';
import type {
  HomeAction,
  HomeActivity,
  HomeApprovals,
  HomeDashboard,
  HomeOverdueReceivables,
  HomeSalesMonth,
  HomeStock,
  HomeTask,
} from '../../lib/types/home.js';
import {
  compactAmount,
  describeActivity,
  dueLabel,
  initialsOf,
  MODULE_LABEL,
  moduleTint,
  percentChange,
  sharePercent,
} from './homeFormat.js';

const fa = (n: number) => n.toLocaleString('fa-IR');

// ------------------------------------------------------------------ KPIs

function Sparkline({ points }: { points: readonly bigint[] }) {
  if (points.length < 2) return null;
  const max = points.reduce((m, p) => (p > m ? p : m), 0n);
  const width = 100;
  const height = 40;
  const step = width / (points.length - 1);
  const y = (p: bigint) =>
    max === 0n ? height - 2 : height - 2 - Number((p * BigInt((height - 4) * 100)) / max) / 100;
  const d = points.map(
    (p, i) => `${i === 0 ? 'M' : 'L'}${(i * step).toFixed(2)},${y(p).toFixed(2)}`,
  );
  return (
    <svg
      viewBox={`0 0 ${width} ${height}`}
      preserveAspectRatio="none"
      width="100%"
      height="100%"
      aria-hidden="true"
      style={{ transform: 'scaleX(-1)' }}
    >
      <path
        d={d.join(' ')}
        fill="none"
        stroke="#fff"
        strokeWidth="1.6"
        vectorEffect="non-scaling-stroke"
      />
    </svg>
  );
}

function SalesTile({
  base,
  data,
  unit,
}: {
  base: string;
  data: HomeSalesMonth;
  unit: AmountDisplayUnit;
}) {
  const amount = compactAmount(data.netSalesRial, unit);
  const change = percentChange(data.netSalesRial, data.previousNetSalesRial);
  let running = 0n;
  const cumulative = data.daily.map((d) => (running += BigInt(d.amountRial)));
  return (
    <Link
      to={`${base}/crm/orders`}
      className="home-kpi home-kpi--hero home-wash"
      title={formatRialFaWithUnit(data.netSalesRial, unit)}
    >
      <div className="home-kpi__top">
        <span className="home-kpi__label">فروش خالص ماه</span>
        <span className="home-chip">
          <Icon name="trend" size="sm" />
        </span>
      </div>
      <div className="home-kpi__hero-body">
        <div style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
          <div className="home-kpi__value">
            <span className="home-kpi__num sk-num">{amount.value}</span>
            <span className="home-kpi__scale">{amount.scale}</span>
          </div>
          <span className="home-kpi__delta">
            {change === null
              ? `${fa(data.invoiceCount)} فاکتور قطعی`
              : `${change >= 0 ? '▲' : '▼'} ${faDigits(Math.abs(change).toString().replace('.', '٫'))}٪ نسبت به ماه قبل`}
          </span>
        </div>
        <div className="home-kpi__spark">
          <Sparkline points={cumulative} />
        </div>
      </div>
    </Link>
  );
}

function ReceivablesTile({
  base,
  data,
  unit,
}: {
  base: string;
  data: HomeOverdueReceivables;
  unit: AmountDisplayUnit;
}) {
  const amount = compactAmount(data.overdueRial, unit);
  const share = sharePercent(data.overdueRial, data.totalOpenRial);
  return (
    <Link
      to={`${base}/finance/ar`}
      data-module="finance"
      className="home-kpi home-wash"
      title={formatRialFaWithUnit(data.overdueRial, unit)}
    >
      <div className="home-kpi__top">
        <span className="home-kpi__label">دریافتنی سررسید گذشته</span>
        <span className="home-chip">
          <Icon name="finance" size="sm" />
        </span>
      </div>
      <div className="home-kpi__value">
        <span className="home-kpi__num sk-num">{amount.value}</span>
        <span className="home-kpi__scale">{amount.scale}</span>
      </div>
      <div className="home-kpi__foot">
        <div className="home-kpi__row">
          <span
            style={{
              fontWeight: 600,
              color: data.debtorCount > 0 ? 'var(--sk-danger)' : 'var(--sk-fg-muted)',
            }}
          >
            {fa(data.debtorCount)} مشتری بدهکار
          </span>
          <span className="sk-num">{fa(share)}٪ از کل دریافتنی</span>
        </div>
        <div className="home-kpi__bar">
          <span style={{ width: `${share}%` }} />
        </div>
      </div>
    </Link>
  );
}

function StockTile({ base, data }: { base: string; data: HomeStock }) {
  const filled = Math.min(4, data.zeroAvailableCount);
  return (
    <Link to={`${base}/inventory`} data-module="inventory" className="home-kpi home-wash">
      <div className="home-kpi__top">
        {/* No reorder point exists in the product model yet, so the honest
            signal is «قابل استفاده صفر», the board's own first legend item. */}
        <span className="home-kpi__label">کالای بدون موجودی قابل استفاده</span>
        <span className="home-chip">
          <Icon name="box" size="sm" />
        </span>
      </div>
      <div className="home-kpi__value">
        <span className="home-kpi__num sk-num">{fa(data.zeroAvailableCount)}</span>
      </div>
      <div className="home-kpi__foot">
        <div className="home-kpi__row">
          <span>{fa(data.activeReservationCount)} رزرو فعال</span>
          <span style={{ display: 'flex', gap: 3 }} aria-hidden="true">
            {[0, 1, 2, 3].map((i) => (
              <span
                key={i}
                style={{
                  width: 12,
                  height: 12,
                  borderRadius: 3,
                  boxSizing: 'border-box',
                  ...(i < filled
                    ? {
                        background: 'color-mix(in oklch, var(--sk-mod-c) var(--sk-mod-k), #0a0c10)',
                      }
                    : {
                        border: '1.5px solid color-mix(in oklch, var(--sk-mod-c) 60%, transparent)',
                      }),
                }}
              />
            ))}
          </span>
        </div>
      </div>
    </Link>
  );
}

function ApprovalsTile({ data }: { data: HomeApprovals }) {
  const parts = [
    { key: 'journals', label: 'سند مالی', value: data.journals, color: 'var(--sk-success)' },
    {
      key: 'invoices',
      label: 'فاکتور فروش',
      value: data.salesInvoices,
      color: 'var(--sk-chart-6)',
    },
    {
      key: 'requests',
      label: 'درخواست خرید',
      value: data.purchaseRequests,
      color: 'var(--sk-chart-5)',
    },
    { key: 'orders', label: 'سفارش خرید', value: data.purchaseOrders, color: 'var(--sk-info)' },
  ].filter((p): p is typeof p & { value: number } => p.value !== null);
  const total = parts.reduce((sum, p) => sum + p.value, 0);
  return (
    <div className="home-kpi home-wash">
      <div className="home-kpi__top">
        <span className="home-kpi__label">در انتظار تأیید شما</span>
        <span className="home-chip">
          <Icon name="check" size="sm" />
        </span>
      </div>
      <div className="home-kpi__value">
        <span className="home-kpi__num sk-num">{fa(total)}</span>
      </div>
      <div className="home-kpi__foot">
        {total > 0 ? (
          <div className="home-kpi__split" aria-hidden="true">
            {parts
              .filter((p) => p.value > 0)
              .map((p) => (
                <span key={p.key} style={{ flex: p.value, background: p.color }} />
              ))}
          </div>
        ) : null}
        <div className="home-kpi__legend">
          {parts.map((p) => (
            <span key={p.key}>
              <i style={{ background: p.color }} />
              {fa(p.value)} {p.label}
            </span>
          ))}
        </div>
      </div>
    </div>
  );
}

// --------------------------------------------------------- needs action

const ACTION_ICON: Record<HomeAction['module'], SakhtarIconName> = {
  finance: 'finance',
  procurement: 'cart',
  crm: 'sales',
  inventory: 'inventory',
  projects: 'projects',
};

function actionText(
  action: HomeAction,
  unit: AmountDisplayUnit,
): { readonly title: string; readonly meta: string | null } {
  const n = fa(action.count);
  const d = action.detail;
  switch (action.kind) {
    case 'journals-awaiting-approval':
      return {
        title: `${n} سند حسابداری در وضعیت «ارسال‌شده» منتظر تأیید شماست`,
        meta: d['oldestSubmittedAt']
          ? `قدیمی‌ترین: ${relativeWhen(String(d['oldestSubmittedAt']), { withTime: false })}`
          : null,
      };
    case 'sales-invoices-awaiting-approval':
      return { title: `${n} فاکتور فروش منتظر تأیید شماست`, meta: null };
    case 'purchase-requests-awaiting-approval':
      return {
        title: `${n} درخواست خرید منتظر تأیید است`,
        meta:
          d['estimatedRial'] && d['estimatedRial'] !== '0'
            ? `جمع مبلغ برآوردی: ${formatRialFaWithUnit(String(d['estimatedRial']), unit)}`
            : null,
      };
    case 'purchase-orders-awaiting-approval':
      return {
        title: `${n} سفارش خرید منتظر تأیید است`,
        meta:
          d['totalRial'] && d['totalRial'] !== '0'
            ? `جمع مبلغ: ${formatRialFaWithUnit(String(d['totalRial']), unit)}`
            : null,
      };
    case 'crm-follow-ups-due':
      return {
        title: `${n} پیگیری روی فرصت‌های فروش تا پایان امروز سررسید دارد`,
        meta:
          Number(d['overdue'] ?? 0) > 0 ? `${fa(Number(d['overdue']))} مورد عقب افتاده است` : null,
      };
    case 'crm-activities-due':
      return {
        title: `${n} فعالیت فروش تا پایان امروز سررسید دارد`,
        meta:
          Number(d['overdue'] ?? 0) > 0 ? `${fa(Number(d['overdue']))} مورد عقب افتاده است` : null,
      };
    case 'zero-available-stock':
      return {
        title: `موجودی قابل استفادهٔ ${n} کالا به صفر رسیده است`,
        meta:
          d['dependentOrders'] !== null && Number(d['dependentOrders'] ?? 0) > 0
            ? `${fa(Number(d['dependentOrders']))} سفارش فروش تأییدشده به این کالاها وابسته است`
            : null,
      };
    case 'overdue-receivables':
      return {
        title: `${n} مشتری دریافتنی سررسید گذشته دارند`,
        meta: `جمع: ${formatRialFaWithUnit(String(d['overdueRial'] ?? '0'), unit)}`,
      };
  }
}

function NeedsAction({
  base,
  actions,
  unit,
}: {
  base: string;
  actions: readonly HomeAction[];
  unit: AmountDisplayUnit;
}) {
  const [expanded, setExpanded] = useState(false);
  const visible = expanded ? actions : actions.slice(0, 4);
  return (
    <V2Card>
      <V2CardHeader
        icon="bell"
        title="نیازمند اقدام"
        subtitle="بر اساس دسترسی شما"
        actions={
          actions.length > 4 ? (
            <V2Button size="sm" variant="secondary" onClick={() => setExpanded((v) => !v)}>
              {expanded ? 'کمتر' : `همه (${fa(actions.length)})`}
            </V2Button>
          ) : undefined
        }
      />
      {actions.length === 0 ? (
        <V2EmptyState
          compact
          icon="check"
          title="موردی برای اقدام شما نیست"
          description="هر چیزی که منتظر تأیید یا پیگیری شما باشد، اینجا نمایش داده می‌شود."
        />
      ) : (
        <div>
          {visible.map((action) => {
            const text = actionText(action, unit);
            return (
              <div
                key={action.kind}
                className="home-action"
                data-module={moduleTint(action.module)}
              >
                <span className="home-action__icon home-wash">
                  <Icon name={ACTION_ICON[action.module]} size="md" />
                </span>
                <div className="home-action__body">
                  <div className="home-action__title">{text.title}</div>
                  <div className="home-action__meta">
                    <span className="home-action__module">{MODULE_LABEL[action.module]}</span>
                    {text.meta ? (
                      <>
                        <span aria-hidden="true">·</span>
                        <span>{text.meta}</span>
                      </>
                    ) : null}
                  </div>
                </div>
                <Link to={`${base}${action.href}`}>
                  <V2Button size="sm" variant="secondary">
                    بررسی
                  </V2Button>
                </Link>
              </div>
            );
          })}
        </div>
      )}
    </V2Card>
  );
}

// ------------------------------------------------------------ shortcuts

export interface Shortcut {
  readonly id: string;
  readonly label: string;
  readonly href: string;
  readonly icon: SakhtarIconName;
  readonly module: string | undefined;
  readonly navigate?: boolean;
}

function Shortcuts({ base, shortcuts }: { base: string; shortcuts: readonly Shortcut[] }) {
  if (shortcuts.length === 0) return null;
  return (
    <V2Card>
      <div id="home-shortcuts">
        <V2CardHeader icon="plus" title="میان‌برها" />
      </div>
      <div className="home-shortcuts">
        {shortcuts.map((s) => (
          <Link
            key={s.id}
            to={`${base}${s.href}`}
            className="home-shortcut home-wash"
            data-module={s.module}
          >
            <span className="home-chip">
              <Icon name={s.icon} size="sm" />
            </span>
            <span className="home-shortcut__label">{s.label}</span>
            <span className="home-shortcut__go" aria-hidden="true">
              <Icon name={s.navigate ? 'more' : 'plus'} size="sm" />
            </span>
          </Link>
        ))}
      </div>
    </V2Card>
  );
}

// -------------------------------------------------------------- my tasks

async function completeTask(base: string, task: HomeTask): Promise<void> {
  if (task.kind === 'project-task') {
    await apiPatch(`${base}/api/v1/projects/tasks/${encodeURIComponent(task.ref)}`, {
      status: 'done',
    });
  } else if (task.kind === 'todo') {
    await apiPatch(`${base}/api/v1/projects/todos/${encodeURIComponent(task.ref)}`, {
      status: 'done',
    });
  } else {
    await apiPost(`${base}/api/v1/crm/activities/${encodeURIComponent(task.ref)}/complete`, {});
  }
}

function MyTasks({
  base,
  data,
  onChanged,
}: {
  base: string;
  data: NonNullable<HomeDashboard['myTasks']>;
  onChanged: () => void;
}) {
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function done(task: HomeTask) {
    setBusy(task.ref);
    setError(null);
    try {
      await completeTask(base, task);
      onChanged();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'ثبت انجام کار ممکن نشد.');
    } finally {
      setBusy(null);
    }
  }

  return (
    <V2Card>
      <V2CardHeader
        icon="check"
        title="کارهای من"
        actions={
          data.total > 0 ? <span className="home-count sk-num">{fa(data.total)}</span> : undefined
        }
      />
      {data.items.length === 0 ? (
        <V2EmptyState compact icon="tasks" title="کار بازی به شما سپرده نشده است" />
      ) : (
        <div>
          {data.items.map((task) => {
            const due = dueLabel(task.due, task.dueKind);
            return (
              <div
                key={`${task.kind}-${task.ref}`}
                className="home-task"
                data-module={moduleTint(task.module)}
              >
                <input
                  className="sk-check"
                  type="checkbox"
                  aria-label={`انجام شد: ${task.title}`}
                  checked={false}
                  disabled={!task.canComplete || busy !== null}
                  onChange={() => void done(task)}
                />
                <div className="home-task__body">
                  <Link to={`${base}${task.href}`} className="home-task__title">
                    {task.title}
                  </Link>
                  <div className="home-task__module">
                    <i />
                    {task.module === 'crm' ? 'فروش' : 'پروژه'}
                  </div>
                </div>
                {due ? (
                  <span
                    className={`home-due${due.tone === 'late' ? ' home-due--late' : due.tone === 'today' ? ' home-due--today' : ''}`}
                  >
                    {due.label}
                  </span>
                ) : null}
              </div>
            );
          })}
        </div>
      )}
      {error ? (
        <div role="alert" style={{ padding: '8px 14px', fontSize: 12, color: 'var(--sk-danger)' }}>
          {error}
        </div>
      ) : null}
    </V2Card>
  );
}

// ------------------------------------------------------- recent activity

function RecentActivity({ items }: { items: readonly HomeActivity[] }) {
  return (
    <V2Card>
      <V2CardHeader
        icon="trend"
        title="فعالیت اخیر"
        actions={
          <span className="home-live">
            <i />
            به‌روزرسانی خودکار
          </span>
        }
      />
      {items.length === 0 ? (
        <V2EmptyState compact icon="clock" title="هنوز فعالیتی ثبت نشده است" />
      ) : (
        <div className="home-feed">
          {items.map((item) => {
            const text = describeActivity(item.action);
            return (
              <div key={item.id} className="home-feed__item" data-module={moduleTint(item.module)}>
                <div className="home-feed__rail">
                  <V2Avatar initials={initialsOf(item.actorName)} size="sm" />
                  <span className="home-feed__line" />
                </div>
                <div className="home-feed__body">
                  <div className="home-feed__text">
                    {text.subject}{' '}
                    {item.recordRef ? (
                      <span className="ltr sk-num">{faDigits(item.recordRef)}</span>
                    ) : null}{' '}
                    {text.verb}
                  </div>
                  <div className="home-feed__meta" title={displayJalaliDateTime(item.occurredAt)}>
                    {item.actorName} · {relativeWhen(item.occurredAt)}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </V2Card>
  );
}

// ------------------------------------------------------------------ page

export function HomeDashboardView({
  base,
  data,
  unit,
  shortcuts,
  onChanged,
}: {
  base: string;
  data: HomeDashboard;
  unit: AmountDisplayUnit;
  shortcuts: readonly Shortcut[];
  onChanged: () => void;
}): ReactNode {
  const tiles = [
    data.salesMonth ? (
      <SalesTile key="sales" base={base} data={data.salesMonth} unit={unit} />
    ) : null,
    data.overdueReceivables ? (
      <ReceivablesTile key="ar" base={base} data={data.overdueReceivables} unit={unit} />
    ) : null,
    data.stock ? <StockTile key="stock" base={base} data={data.stock} /> : null,
    data.approvals ? <ApprovalsTile key="approvals" data={data.approvals} /> : null,
  ].filter(Boolean);

  const side = [
    data.myTasks ? (
      <MyTasks key="tasks" base={base} data={data.myTasks} onChanged={onChanged} />
    ) : null,
    data.activity ? <RecentActivity key="activity" items={data.activity} /> : null,
  ].filter(Boolean);

  return (
    <>
      {tiles.length > 0 ? <div className="home-kpis">{tiles}</div> : null}
      <div className="home-cols">
        <div className="home-col home-col--main">
          <NeedsAction base={base} actions={data.actions} unit={unit} />
          <Shortcuts base={base} shortcuts={shortcuts} />
        </div>
        {side.length > 0 ? <div className="home-col home-col--side">{side}</div> : null}
      </div>
    </>
  );
}
